import { ScanOutlined } from "@ant-design/icons";
import { Input, type InputRef } from "antd";
import { useEffect, useRef, useState } from "react";
import { useI18n } from "@/i18n";
import { soundFor, type ScanResult } from "@/lib/scanSound";

/** Scan fields on screen, in the order they appeared. */
const claimers: object[] = [];

/**
 * A field for barcode scanners. Handheld and Bluetooth scanners type the code
 * like a keyboard and press Enter (keyboard wedge); typing by hand works the
 * same. The field empties and keeps focus after each scan, ready for the next.
 * Every scan answers by sound: onScan returns true / false, or a promise
 * (resolved: success, rejected: failure).
 */
export function ScanInput({
  onScan,
  onEmptyEnter,
  placeholder,
  autoFocus = true,
  disabled,
  size = "large",
}: {
  onScan: (code: string) => ScanResult;
  /** Enter on an empty field, e.g. "use the suggestion". */
  onEmptyEnter?: () => ScanResult;
  placeholder?: string;
  autoFocus?: boolean;
  disabled?: boolean;
  size?: "large" | "middle";
}) {
  const { t } = useI18n();
  const [value, setValue] = useState("");
  const ref = useRef<InputRef>(null);
  useEffect(() => {
    if (autoFocus && !disabled) ref.current?.focus();
  }, [autoFocus, disabled]);
  // A scan typed while a button or the page has the focus (after a click)
  // comes back here, unless another field is being typed in or a dialog is open.
  useEffect(() => {
    if (!autoFocus || disabled) return;
    // With several on a page, the one shown last (the current step) takes it.
    const me = {};
    claimers.push(me);
    const onKey = (e: KeyboardEvent) => {
      if (claimers[claimers.length - 1] !== me) return;
      const a = document.activeElement as HTMLElement | null;
      if (a && (a.tagName === "INPUT" || a.tagName === "TEXTAREA" || a.tagName === "SELECT" || a.isContentEditable)) return;
      if (e.ctrlKey || e.metaKey || e.altKey || e.key.length !== 1) return;
      const open = [...document.querySelectorAll<HTMLElement>(".ant-modal-wrap, .ant-drawer-open")].filter((m) => m.offsetParent !== null || m.classList.contains("ant-drawer-open"));
      if (open.some((m) => !m.contains(ref.current?.input ?? null))) return;
      ref.current?.focus();
    };
    document.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("keydown", onKey, true);
      claimers.splice(claimers.indexOf(me), 1);
    };
  }, [autoFocus, disabled]);
  return (
    <Input
      ref={ref}
      size={size}
      prefix={<ScanOutlined />}
      value={value}
      disabled={disabled}
      placeholder={placeholder ?? t("wms.scanPlaceholder")}
      onChange={(e) => setValue(e.target.value)}
      onPressEnter={() => {
        const code = value.trim();
        setValue("");
        if (code) soundFor(onScan(code));
        else if (onEmptyEnter) soundFor(onEmptyEnter());
      }}
      autoComplete="off"
      spellCheck={false}
    />
  );
}
